import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreLoader from "@store/Components/Store/StoreLoader";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@store/Overrides/Components/ProductCarouselProduct";
import { getArrowProps, getArrowWrapperProps, productCarouselProps } from "@store/theme/props/productCarousel";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import updateCssValue, { Operators } from "@store/utilities/updateCssValue";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { createRef, FC, useCallback, useContext, useEffect, useState } from "react";
import { css, useTheme } from "styled-components";

const slideSpacing = "60px";

interface Props {
    id: string;
    products?: ProductModel[];
    maxNumberOfColumns?: number;
    showImage?: boolean;
    showTitle?: boolean;
    showBrandName?: boolean;
    showPartNumbers?: boolean;
    showPrice?: boolean;
    showAddToCart?: boolean;
    showAddToList?: boolean;
}

export interface StoreProductCarouselStyles {
    skipCarouselButton?: SkipNavStyles;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    noResults?: TypographyPresentationProps;
}

export const storeProductCarouselStyles: StoreProductCarouselStyles = {
    mainWrapper: productCarouselProps.mainWrapper,
    innerWrapper: productCarouselProps.innerWrapper,
    prevArrowWrapper: getArrowWrapperProps("left", true),
    prevArrowButton: getArrowProps("left"),
    nextArrowWrapper: getArrowWrapperProps("right", true),
    nextArrowButton: getArrowProps("right"),
    carouselContainer: {
        ...productCarouselProps.carouselContainer,
        css: css`
            ${productCarouselProps.carouselContainer?.css}
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            ${productCarouselProps.carouselSlidesContainer?.css}
            margin: 0 -${updateCssValue(slideSpacing, Operators.Divide, 2)};
        `,
    },
    carouselSlide: {
        css: css`
            ${productCarouselProps.carouselSlide?.css}
            padding: 0 ${updateCssValue(slideSpacing, Operators.Divide, 2)};
            border-right-width: 0;
            border-right-style: solid;
            border-right-color: ${getColor("common.border")};

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, "1px"], properties: ["border-right-width"] }],
                    mediaOptions.min,
                )}

            &:last-child {
                border-right-width: 0;
            }
        `,
    },
    carouselSlideInner: productCarouselProps.carouselSlideInner,
    noResults: {
        css: css`
            display: block;
            text-align: center;
        `,
    },
    carouselProductStyles: {
        productImageStyles: productCarouselProps.productImageStyles,
        productBrandWrapper: productCarouselProps.productBrandWrapper,
        productBrandStyles: productCarouselProps.productBrandStyles,
        productDescriptionStyles: productCarouselProps.productDescriptionStyles,
        partNumberText: productCarouselProps.partNumberText,
        contentWrapper: {
            css: css`
                padding: 0 20px;
            `,
        },
        productPriceWrapper: productCarouselProps.productPriceWrapper,
        productPrice: productCarouselProps.productPrice,
    },
};

const styles = storeProductCarouselStyles;

const StoreProductCarousel: FC<Props> = ({
    id,
    products,
    maxNumberOfColumns = 4,
    showImage = true,
    showTitle = true,
    showBrandName = true,
    showPartNumbers = true,
    showPrice = true,
    showAddToCart = true,
    showAddToList = true,
}) => {
    const { breakpoints } = useTheme() as BaseTheme;
    const shellContext = useContext(ShellContext);
    const afterCarousel = createRef<HTMLSpanElement>();
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const productsNumber = products?.length;
    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns, productsNumber]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            containScroll: "trimSnaps",
        });
        setCanScroll();
    }, [embla, slidesToScroll, products]);

    if (!products) {
        return <StoreLoader />;
    }

    if (products.length === 0) {
        return <Typography {...styles.noResults}>{translate("NoResults_NewArrivals")}</Typography>;
    }

    const showBrandBlocks = showBrandName && products.some(o => !!o.brand);

    const arrowAlignmentStyles = css`
        padding-top: calc((100% + ${slideSpacing}) / ${slidesToScroll} - ${slideSpacing});
    `;
    const prevArrowWrapperStyles = css`
        ${arrowAlignmentStyles}
        ${styles.prevArrowWrapper?.css}
    `;
    const nextArrowWrapperStyles = css`
        ${arrowAlignmentStyles}
        ${styles.nextArrowWrapper?.css}
    `;

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper {...styles.mainWrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    {products.length > slidesToScroll && (
                        <StyledWrapper css={prevArrowWrapperStyles}>
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            ></Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                            {products.map(product => (
                                <StyledWrapper
                                    {...styles.carouselSlide}
                                    key={product.id}
                                    style={{ width: `calc(100% / ${slidesToScroll})` }}
                                >
                                    <StyledWrapper
                                        {...styles.carouselSlideInner}
                                        id={`product_${product.id}`}
                                        data-test-selector="productContainer"
                                    >
                                        <ProductCarouselProduct
                                            carouselId={id}
                                            product={product}
                                            showImage={showImage}
                                            showBrand={showBrandBlocks}
                                            showTitle={showTitle}
                                            showPartNumbers={showPartNumbers}
                                            showPrice={showPrice}
                                            showAddToCart={showAddToCart}
                                            showAddToList={showAddToList}
                                            overridedStyles={styles.carouselProductStyles}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                    {products.length > slidesToScroll && (
                        <StyledWrapper css={nextArrowWrapperStyles}>
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                            ></Button>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

export default StoreProductCarousel;
